'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import { Loader } from '@repo/ui/Loader';

import s from './Button.module.css';

export const buttonVariantOptions = [
  'link',
  'primary',
  'secondary',
  'icon',
  'micro',
] as const;
export type ButtonVariant = (typeof buttonVariantOptions)[number];

export const buttonSizeOptions = ['xs', 'sm', 'md', 'lg'] as const;
export type ButtonSize = (typeof buttonSizeOptions)[number];

export interface ButtonBaseProps extends ComponentPropsWithRef<'button'> {
  ariaDisabled?: boolean;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  size?: ButtonSize;
}

export interface ButtonProps extends ButtonBaseProps {
  asChild?: boolean;
  children: ReactNode;
  isFullWidth?: boolean;
  variant?: ButtonVariant;
}

export const buttonVariants = cva(s.button, {
  variants: {
    variant: {
      link: s.linkVariant,
      primary: s.primaryVariant,
      secondary: s.secondaryVariant,
      icon: s.iconVariant,
      micro: s.microVariant,
    },
    size: {
      xs: s.xsSize,
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
    },
    isFullWidth: {
      true: s.isFullWidth,
    },
    isLoading: {
      true: s.isLoading,
    },
  },
});

export const Button = (props: ButtonProps) => {
  const {
    ariaDisabled,
    asChild = false,
    children,
    className,
    disabled = false,
    isFullWidth = false,
    isLoading = false,
    loadingLabel = 'Loading',
    onClick,
    size = 'md',
    type = 'button',
    variant = 'primary',
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const handleClick: ComponentPropsWithRef<'button'>['onClick'] = (e) => {
    if (onClick && !disabled && !isLoading) {
      onClick(e);
    }
  };

  const Component = asChild ? Slot : 'button';
  const htmlButtonType = Component === 'button' ? type : undefined;
  const appearsDisabled = ariaDisabled || disabled ? true : undefined;

  const loader = isLoading && (
    <Loader
      key="loader"
      label={loadingLabel}
      size="font"
      className={s.loader}
    />
  );

  let content;

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      throw new Error('Button child must be a valid React element');
    }
    const childElement = child;
    // Clone the child but add the span inside it and the loader after
    content = cloneElement(
      childElement,
      attrs,
      <>
        <span className={s.children}>{childElement.props.children}</span>
        {loader}
      </>,
    );
  } else {
    content = (
      <>
        <span className={s.children}>{children}</span>
        {loader}
      </>
    );
  }

  return (
    <Component
      aria-busy={isLoading}
      aria-disabled={appearsDisabled}
      className={clsx(
        buttonVariants({
          size,
          variant,
          isFullWidth,
          isLoading,
          className,
        }),
      )}
      disabled={disabled}
      onClick={handleClick}
      type={htmlButtonType}
      {...attrs}
    >
      {content}
    </Component>
  );
};
